import { defaultHomepageSettings, type HomepageSettings } from '../store/slices/homepageSlice'
import { getSupabaseBrowserClient } from '../lib/supabaseClient'
import { getApiUrl, normalizeApiAssetPath } from '../utils/api'

type HomepageMappedKey = 'heroTitle' | 'heroSubtitle' | 'heroDescription'

type RoomRow = {
  id: number
  unit_type: string
  high_season_rate: number
  mid_season_rate: number
  low_season_rate: number
  min_stay: number | null
  admin_fee: number | null
  breakage_deposit: number | null
  capacity: number
  amenities: unknown
  description: string | null
  color: string | null
  image_urls?: unknown
  hero_image_index?: number | null
  rooms: Array<{
    id: number
    status: string | null
    room_number: string | null
  }> | null
}

type HomepageSettingsRow = {
  setting_key: string
  setting_value: unknown
}

type PublicRoom = {
  id: number
  name: string
  type: string
  roomNumber: string
  capacity: number
  description: string
  amenities: string[]
  images: string[]
  hero_image_index: number
  pricing: {
    high_season: number
    mid_season: number
    low_season: number
    admin_fee: number
    breakage_deposit: number
    min_stay: number
  }
  price: number
  color: string
  available: boolean
  available_rooms: number
  total_rooms: number
}

type SeasonalCalendarRow = {
  id: number
  start_date: string
  end_date: string
  season: 'low' | 'mid' | 'high'
  room_type_id: number | null
}

type PublicAvailabilityRow = {
  id: string
  check_in: string
  check_out: string
  room_type_id: number | null
  status: string | null
}

type PublicGuestFeedbackRow = {
  id?: string
  guestName?: string
  location?: string
  stayLabel?: string
  quote?: string
  sourceType?: 'booking' | 'enquiry' | 'website'
  sourceLabel?: string
  submittedAt?: string
  rating?: number | null
  sourceUrl?: string | null
}

export type PublicSeasonPeriod = {
  id: string
  startDate: string
  endDate: string
  season: 'low' | 'mid' | 'high'
  roomTypeId: number | null
}

export type PublicAvailabilityBooking = {
  id: string
  checkIn: string
  checkOut: string
  roomTypeId: number | null
  status: string
}

export type PublicGuestReview = {
  id: string
  guestName: string
  location: string
  stayLabel: string
  quote: string
  sourceType: 'booking' | 'enquiry' | 'website'
  sourceLabel: string
  submittedAt: string
  rating?: number | null
  sourceUrl?: string | null
}

export type PublicRoomTypeAvailability = {
  available: boolean
  availableRooms: number
  minimumStay: number
  requestedNights: number
  reason?: string
}

const homepageKeyMap: Record<string, HomepageMappedKey> = {
  hero_title: 'heroTitle',
  hero_subtitle: 'heroSubtitle',
  hero_description: 'heroDescription',
}

const roomImageFallbacks: Record<string, string[]> = {
  '4 Sleeper Unit': ['/room-4-sleeper.jpg'],
  '4-Sleeper Unit': ['/room-4-sleeper.jpg'],
  '6 Sleeper Unit': ['/room-6-sleeper.jpg'],
  '6-Sleeper Unit': ['/room-6-sleeper.jpg'],
  'Bigger 6 sleeper Unit': ['/room-6-sleeper.jpg', '/room-2-sleeper.jpg'],
  'Bigger 6-Sleeper Unit': ['/room-6-sleeper.jpg', '/room-2-sleeper.jpg'],
}

const normalizeDate = (value: string) => String(value || '').split('T')[0]

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

const normalizeImages = (unitType: string, value: unknown): string[] => {
  const providedImages = toStringArray(value).map((entry) => normalizeApiAssetPath(entry))
  if (providedImages.length > 0) {
    return providedImages
  }

  return (roomImageFallbacks[unitType] || []).map((entry) => normalizeApiAssetPath(entry))
}

const normalizeHomepageImage = (value: unknown, fallbackValue: string): string => {
  if (typeof value !== 'string' || !value.trim()) {
    return fallbackValue
  }

  return normalizeApiAssetPath(value)
}

const normalizeHomepageSettings = (settings: HomepageSettings): HomepageSettings => ({
  ...settings,
  featureCards: settings.featureCards.map((card, index) => ({
    ...card,
    image: normalizeHomepageImage(card.image, defaultHomepageSettings.featureCards[index]?.image || ''),
  })),
  unitDescriptions: settings.unitDescriptions.map((unit, index) => ({
    ...unit,
    image: normalizeHomepageImage(unit.image, defaultHomepageSettings.unitDescriptions[index]?.image || ''),
  })),
})

const normalizeHomepageRows = (rows: HomepageSettingsRow[]): HomepageSettings => {
  const singleObjectRow = rows.find((row) => row.setting_key === 'homepage_content_settings')
  if (singleObjectRow && singleObjectRow.setting_value && typeof singleObjectRow.setting_value === 'object') {
    return normalizeHomepageSettings({
      ...defaultHomepageSettings,
      ...(singleObjectRow.setting_value as Partial<HomepageSettings>),
    })
  }

  const mapped = rows.reduce<Partial<Pick<HomepageSettings, HomepageMappedKey>>>((accumulator, row) => {
    const mappedKey = homepageKeyMap[row.setting_key]
    if (!mappedKey) {
      return accumulator
    }

    const value = row.setting_value
    accumulator[mappedKey] = typeof value === 'string' ? value : String(value ?? '')
    return accumulator
  }, {})

  return normalizeHomepageSettings({
    ...defaultHomepageSettings,
    ...mapped,
    unitDescriptions: defaultHomepageSettings.unitDescriptions.map((unit) => ({
      ...unit,
      image: roomImageFallbacks[unit.unitType]?.[0] || unit.image,
    })),
  })
}

export const fetchHomepageSettingsFromSupabase = async (): Promise<HomepageSettings> => {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase
    .from('homepage_settings')
    .select('setting_key, setting_value')
    .in('setting_key', ['homepage_content_settings', 'hero_title', 'hero_subtitle', 'hero_description'])

  if (error) {
    throw error
  }

  return normalizeHomepageRows((data || []) as HomepageSettingsRow[])
}

export const fetchJsonSettingFromSupabase = async <T extends object>(
  settingKey: string,
  defaults: T,
): Promise<T> => {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase
    .from('homepage_settings')
    .select('setting_value')
    .eq('setting_key', settingKey)
    .maybeSingle()

  if (error) {
    throw error
  }

  const value = data?.setting_value
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return defaults
  }

  return {
    ...defaults,
    ...(value as Partial<T>),
  }
}

export const fetchPublicRoomsFromSupabase = async (): Promise<PublicRoom[]> => {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase
    .from('room_types')
    .select('id, unit_type, high_season_rate, mid_season_rate, low_season_rate, min_stay, admin_fee, breakage_deposit, capacity, amenities, description, color, image_urls, hero_image_index, rooms(id, status, room_number)')
    .eq('is_active', true)
    .order('capacity', { ascending: true })

  if (error) {
    throw error
  }

  return ((data || []) as RoomRow[]).map((roomType) => {
    const roomUnits = Array.isArray(roomType.rooms) ? roomType.rooms : []
    const primaryRoom = roomUnits
      .slice()
      .sort((left, right) => String(left.room_number || '').localeCompare(String(right.room_number || '')))[0] || null
    const availableRooms = roomUnits.filter((room) => (room.status || 'available') === 'available').length
    const totalRooms = roomUnits.length
    const roomNumber = String(primaryRoom?.room_number || '').trim()
    const images = normalizeImages(roomType.unit_type, roomType.image_urls)
    const rawHeroImageIndex = Number(roomType.hero_image_index ?? 0)
    const heroImageIndex = images.length === 0
      ? 0
      : Math.min(Math.max(rawHeroImageIndex, 0), images.length - 1)

    return {
      id: Number(roomType.id),
      name: roomNumber || roomType.unit_type,
      type: roomType.unit_type,
      roomNumber,
      capacity: Number(roomType.capacity || 0),
      description: roomType.description || '',
      amenities: toStringArray(roomType.amenities),
      images,
      hero_image_index: heroImageIndex,
      pricing: {
        high_season: Number(roomType.high_season_rate || 0),
        mid_season: Number(roomType.mid_season_rate || 0),
        low_season: Number(roomType.low_season_rate || 0),
        admin_fee: Number(roomType.admin_fee || 0),
        breakage_deposit: Number(roomType.breakage_deposit || 0),
        min_stay: Number(roomType.min_stay || 1),
      },
      price: Number(roomType.low_season_rate || 0),
      color: roomType.color || 'blue',
      available: availableRooms > 0,
      available_rooms: availableRooms,
      total_rooms: totalRooms,
    }
  })
}

export const fetchSeasonPeriodsFromSupabase = async (): Promise<PublicSeasonPeriod[]> => {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase
    .from('seasonal_calendar')
    .select('id, start_date, end_date, season, room_type_id')
    .order('start_date', { ascending: true })

  if (error) {
    throw error
  }

  return ((data || []) as SeasonalCalendarRow[]).map((row) => ({
    id: String(row.id ?? ''),
    startDate: normalizeDate(String(row.start_date ?? '')),
    endDate: normalizeDate(String(row.end_date ?? '')),
    season: row.season,
    roomTypeId: row.room_type_id == null ? null : Number(row.room_type_id),
  }))
}

export const fetchPublicBookingAvailabilityFromSupabase = async (): Promise<PublicAvailabilityBooking[]> => {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('get_public_booking_availability')

  if (error) {
    throw error
  }

  return ((data || []) as PublicAvailabilityRow[]).map((row) => ({
    id: String(row.id ?? ''),
    checkIn: normalizeDate(String(row.check_in ?? '')),
    checkOut: normalizeDate(String(row.check_out ?? '')),
    roomTypeId: row.room_type_id == null ? null : Number(row.room_type_id),
    status: String(row.status || ''),
  }))
}

export const fetchRoomTypeAvailability = async (
  roomTypeId: number,
  checkIn: string,
  checkOut: string,
): Promise<PublicRoomTypeAvailability> => {
  const response = await fetch(`${getApiUrl()}/api/bookings/availability-check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      room_type_id: roomTypeId,
      check_in: normalizeDate(checkIn),
      check_out: normalizeDate(checkOut),
    }),
  })

  const result = await response.json().catch(() => null)

  if (!response.ok || !result?.success || !result?.data) {
    throw new Error(result?.error || 'Failed to load room availability')
  }

  return {
    available: Boolean(result.data.available),
    availableRooms: Number(result.data.availableRooms || 0),
    minimumStay: Number(result.data.minimumStay || 1),
    requestedNights: Number(result.data.requestedNights || 0),
    reason: typeof result.data.reason === 'string' && result.data.reason.trim() ? result.data.reason : undefined,
  }
}

export const fetchPublicGuestReviewsFromSupabase = async (limit = 6): Promise<PublicGuestReview[]> => {
  const response = await fetch(`${getApiUrl()}/api/guest-feedback?limit=${encodeURIComponent(String(limit))}`)
  const responseText = await response.text()
  const result = responseText ? JSON.parse(responseText) as { success?: boolean; data?: PublicGuestFeedbackRow[]; error?: string } : null

  if (!response.ok || !result?.success || !Array.isArray(result?.data)) {
    throw new Error(result?.error || 'Failed to load public guest reviews')
  }

  return (result.data as PublicGuestFeedbackRow[]).map((entry) => ({
    id: String(entry.id ?? ''),
    guestName: String(entry.guestName ?? 'Guest'),
    location: String(entry.location ?? 'Marina Glen guest'),
    stayLabel: String(entry.stayLabel ?? 'Marina Glen stay'),
    quote: String(entry.quote ?? ''),
    sourceType: entry.sourceType === 'booking' ? 'booking' : entry.sourceType === 'enquiry' ? 'enquiry' : 'website',
    sourceLabel: String(entry.sourceLabel ?? 'Guest feedback'),
    submittedAt: String(entry.submittedAt ?? new Date().toISOString()),
    rating: entry.rating == null ? null : Number(entry.rating),
    sourceUrl: typeof entry.sourceUrl === 'string' && entry.sourceUrl.trim() ? entry.sourceUrl.trim() : null,
  }))
}